import BaseOverlay from './base-overlay.js';
import { createFocusTrap } from 'focus-trap';

/**
 * Configuration object for SideDrawer selectors and constants
 */
const DRAWER_CONFIG = {
    SELECTORS: {
        CONTENT: '.drawer__content, .drawer__inner',
        SCROLL: '[data-drawer-scroll]',
        CLOSE_BUTTON: '[data-toggle-attribute]',
        FOCUSABLE_ELEMENTS: 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    },
    EVENTS: {
        TOGGLE: 'toggle' // Legacy event for backwards compatibility
    }
};

/**
 * SideDrawer component extending BaseOverlay
 * Provides drawer/slide-in panel functionality with body scroll locking and focus trap
 */
export default class SideDrawer extends BaseOverlay {
    constructor() {
        super();
        this.focusTrap = null;
    }

    connectedCallback() {
        super.connectedCallback();
        this.initFocusTrap();
    }

    /**
     * Get the content element for scroll locking
     * @returns {HTMLElement|null}
     */
    getContentElement() {
        return this.querySelector(DRAWER_CONFIG.SELECTORS.CONTENT) || this;
    }

    /**
     * Get the scroll lock target — the element that actually scrolls.
     *
     * body-scroll-lock on iOS hooks touchmove and only allows finger-scrolling
     * inside the element handed to it. If that element isn't the real scroll
     * container, the whole drawer becomes unscrollable on a real device (it still
     * scrolls in the simulator because trackpad scrolling fires wheel, not touch,
     * events). So target the inner [data-drawer-scroll] element when present and
     * only fall back to the content element.
     * @returns {HTMLElement|null}
     */
    getScrollLockTarget() {
        return this.querySelector(DRAWER_CONFIG.SELECTORS.SCROLL) || this.getContentElement();
    }

    /**
     * Drawers should close when clicking outside
     * @returns {boolean}
     */
    shouldCloseOnOutsideClick() {
        return true;
    }

    /**
     * Initialize focus trap for accessibility
     */
    initFocusTrap() {
        this.focusTrap = createFocusTrap(this, {
            allowOutsideClick: true,
            escapeDeactivates: false, // Let BaseOverlay handle Escape key
            fallbackFocus: this,
            initialFocus: () => {
                // Try to focus first interactive element, or close button, or content
                const firstFocusable = this.querySelector(DRAWER_CONFIG.SELECTORS.FOCUSABLE_ELEMENTS);
                const closeButton = this.querySelector(DRAWER_CONFIG.SELECTORS.CLOSE_BUTTON);
                return firstFocusable || closeButton || this.getContentElement() || this;
            },
            onDeactivate: () => {
                // If focus trap deactivates but drawer is still open, close it
                if (this.isOpen) {
                    this.close();
                }
            }
        });
    }

    /**
     * Called when drawer opens
     * Dispatches legacy 'toggle' event for backwards compatibility
     */
    onOpen() {
        // Set ARIA attributes for accessibility
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('role', 'dialog');

        // Activate focus trap
        if (this.focusTrap) {
            this.focusTrap.activate();
        }

        // Dispatch legacy toggle event
        this.dispatchEvent(
            new CustomEvent(DRAWER_CONFIG.EVENTS.TOGGLE, {
                detail: { isOpen: true },
                bubbles: true
            })
        );
    }

    /**
     * Called when drawer closes
     * Dispatches legacy 'toggle' event for backwards compatibility
     */
    onClose() {
        // Remove ARIA attributes
        this.removeAttribute('aria-modal');

        // Deactivate focus trap
        if (this.focusTrap && this.focusTrap.active) {
            this.focusTrap.deactivate();
        }

        // Dispatch legacy toggle event
        this.dispatchEvent(
            new CustomEvent(DRAWER_CONFIG.EVENTS.TOGGLE, {
                detail: { isOpen: false },
                bubbles: true
            })
        );
    }

    /**
     * Cleanup when element is removed from DOM
     */
    destroy() {
        if (this.focusTrap) {
            this.focusTrap.deactivate();
        }
        super.destroy();
    }
}

customElements.define('side-drawer', SideDrawer);
